import type {
  IFileState,
  IValidationResult,
  IChecklistItem,
  IMultiSelectItem,
} from "@/interfaces";
import type { RRuleState, Section, TabErrorSchemaTypes } from "@/types";
import { defineStore } from "pinia";
import joi from "joi";
import TitleSection from "@/views/planner/dialogs/templates/form/sections/TitleSection.vue";
import IconSection from "@/views/planner/dialogs/templates/form/sections/IconSection.vue";
import CategorySection from "@/views/planner/dialogs/templates/form/sections/CategorySection.vue";
import TimeSection from "@/views/planner/dialogs/templates/form/sections/TimeSection.vue";
import ChecklistSection from "@/views/planner/dialogs/templates/form/sections/ChecklistSection.vue";
import { validate } from "@/helpers/forms/validate";
import getTabErrors from "@/helpers/forms/getTabErrors";
import toFileState from "@/helpers/files/toFileState";
import type {
  PlannerSubtaskInput,
  PlannerTemplateInput,
} from "@/graphql/generated-types";
import { useAuthStore } from "@/stores/authStore";
import md5 from "md5";
import toRRuleInput from "@/helpers/rrules/toRRuleInput";

export type PlannerTemplateFormSteps =
  | "title"
  | "icon"
  | "category"
  | "time"
  | "checklist";

export const PLANNER_TEMPLATE_FORM_SECTIONS: Section<PlannerTemplateFormSteps>[] =
  [
    {
      title: "global.title",
      value: "title",
      component: TitleSection,
      isRequired: true,
    },
    {
      title: "global.pictogram.title",
      value: "icon",
      component: IconSection,
    },
    {
      title: "global.category",
      value: "category",
      component: CategorySection,
      isRequired: true,
    },
    {
      title: "templates.time",
      value: "time",
      component: TimeSection,
      isRequired: true,
    },
    {
      title: "global.checklist",
      value: "checklist",
      component: ChecklistSection,
    },
  ];

export const TITLE_VALIDATION_SCHEMA = {
  title: joi.string().max(50).required(),
};

export const TIME_VALIDATION_SCHEMA = {
  startTime: joi.string().min(5).max(5).required(),
};

const VALIDATION_SCHEMA = joi.object({
  ...TITLE_VALIDATION_SCHEMA,
  ...TIME_VALIDATION_SCHEMA,
});

const TAB_FIELDS_SCHEMA: TabErrorSchemaTypes = {
  title: ["title"],
  category: ["category"],
  time: ["startTime"],
};

interface IForm {
  id?: string;
  title: string;
  pictogram?: IMultiSelectItem;
  category?: string;
  startTime: string;
  duration: string;
  rruleState?: RRuleState;
  checklist: IChecklistItem[];
}

interface State {
  section: PlannerTemplateFormSteps;
  validation: IValidationResult | null;
  tabErrors: Record<string, boolean>;
  imageToUpload: IFileState[];
  searchText: string;
  form: IForm;
  originalHash?: string;
}

export const usePlannerTemplateForm = defineStore("PLANNER_TEMPLATE_FORM", {
  state: (): State => {
    return {
      section: "title",
      validation: null,
      tabErrors: {},
      imageToUpload: [],
      searchText: "",
      form: {
        title: "",
        category: "",
        startTime: "12:00",
        duration: "15",
        checklist: [],
      },
    };
  },
  getters: {
    isEditing(state) {
      return !!state.form.id;
    },
    nextButtonKey(state) {
      if (state.section === "checklist") {
        return "global.save";
      }

      return "global.next";
    },
    selectedImage(state) {
      if (state.imageToUpload.length > 0) {
        return state.imageToUpload[0];
      }

      if (state.form.pictogram?.imageURL) {
        return toFileState(
          state.form.pictogram.imageURL,
          "image",
          state.form.pictogram.id,
        );
      }

      return undefined;
    },
    currentHash(state) {
      const data = {
        ...state.form,
      };

      return md5(JSON.stringify(data));
    },
  },
  actions: {
    setOriginalHash() {
      this.originalHash = this.currentHash;
    },
    getTemplateInput() {
      this.validation = validate(VALIDATION_SCHEMA, this.form);
      this.tabErrors = getTabErrors(TAB_FIELDS_SCHEMA, this.validation.errors);

      if (!this.validation.isValid) {
        return;
      }

      const authStore = useAuthStore();

      const template: PlannerTemplateInput = {
        id: this.form.id,
        institution: authStore.institutionId,
        title: this.form.title,
        category: this.form.category,
        duration: parseInt(this.form.duration),
        // pictogram?: InputMaybe<Scalars["ID"]["input"]>;
        start: this.form.startTime,
        subtasks: this.form.checklist.map((item) => {
          return {
            title: item.title,
          } as PlannerSubtaskInput;
        }),
      };

      // That means it's unselected
      if (template.category?.trim().length === 0) {
        template.category = null;
      }

      // Setting the pictogram ID
      if (this.form.pictogram?.imageURL) {
        template.pictogram = this.form.pictogram.id;
        template.image = null;
      } else {
        template.pictogram = null;
      }

      if (this.form.rruleState) {
        template.rset = {
          excludedDates: [],
          rule: toRRuleInput(this.form.rruleState),
        };
      } else if (this.form.id) {
        // If this is an update, we should clear the rrule on unselect
        template.rset = null;
      }

      return template;
    },
    handleRemoveFile() {
      this.imageToUpload = [];
      this.form.pictogram = undefined;
    },
  },
});
